import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toIntlDateTimeFormat } from '@zedoc/date';
import Card from '../../../components/Card';
import Table from '../../../components/lists/Table';
import {
  INTERNAL_STATUS_PENDING,
  CLINICIAN_OPTION_ALL,
  getAppointmentStatusOptions,
  CLINICAL_EVENT_TYPE_APPOINTMENT,
  CLINICAL_EVENT_TYPE_PROCEDURE,
  CLINICAL_EVENT_TYPE_ENROLMENT,
  APPOINTMENT_STATUS_OPTION_ALL,
  PROCEDURE_STATUS_OPTION_ALL,
  ENROLMENT_STATUS_OPTION_ALL,
  getProcedureStatusOptions,
  getClinicalEventTypeOptions,
  getEnrolmentStatusOptions,
} from '../helpers';
import { getAppointmentColumns } from '../Appointments/Appointments';
import { getProcedureColumns } from '../Procedures/Procedures';
import { getEnrolmentColumns } from '../enrolments';
import Layout from '../components/Layout';
import FormFieldText from '../../../components/forms/FormFieldText';
import FormFieldSearch from '../../../components/forms/FormFieldSearch';
import {
  AppointmentDetails,
  ProcedureDetails,
  EnrolmentDetails,
} from '../../../components/dialogs/EpisodeOfCareDetails';
import useReviewQueueItems from '../hooks/useReviewQueueItems';
import useClinicians from '../hooks/useClinicians';

const NeedsReviewQueue = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const [currentRecord, setCurrentRecord] = useState();
  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const [patientName, setPatientName] = useState('');
  const [patientNameInput, setPatientNameInput] = useState('');
  const [clinicianId, setClinicianId] = useState(null);
  const [clinicalEventType, setClinicalEventType] = useState(
    CLINICAL_EVENT_TYPE_APPOINTMENT,
  );
  const [clinicalEventStatus, setClinicalEventStatus] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { clinicians, loading: cliniciansLoading } = useClinicians();

  const { reviewQueueItems, total, loading, error, resolveItem, deleteItem } =
    useReviewQueueItems({
      page,
      pageSize,
      patientName: patientName || undefined,
      clinicianId: clinicianId || undefined,
      domainId: clinicalEventType,
      status: clinicalEventStatus !== 'all' ? clinicalEventStatus : undefined,
    });

  const pendingRecords = useMemo(() => {
    return reviewQueueItems
      .filter(
        (item) =>
          item.domain_id?.toLowerCase() === 'visit' ||
          item.domain_id?.toLowerCase() === 'procedure' ||
          item.domain_id?.toLowerCase() === 'enrolment',
      )
      .map((item) => {
        const domainLower = item.domain_id?.toLowerCase();
        const isVisit = domainLower === 'visit';
        const isProcedure = domainLower === 'procedure';
        const isEnrolment = domainLower === 'enrolment';
        const { payload, severity, type, reasons } = item;

        // Determine domain for UI
        let domain = 'visit';
        if (isProcedure) domain = 'procedure';
        if (isEnrolment) domain = 'enrolment';

        // Determine eventId based on domain
        let eventId;
        if (isVisit) {
          eventId = payload.source_visit_occurrence_id;
        } else if (isProcedure) {
          eventId = payload.source_procedure_id;
        } else if (isEnrolment) {
          eventId = payload.person_source_value || payload.person_id;
        }

        // Determine status based on domain
        let status;
        if (isVisit) {
          status = payload.visit_status?.toLowerCase() || 'pending';
        } else if (isProcedure) {
          status = payload.status?.toLowerCase() || 'pending';
        } else if (isEnrolment) {
          status = 'active';
        }

        // Determine start date based on domain
        let start = null;
        if (isVisit && payload.start_datetime) {
          start = new Date(payload.start_datetime);
        } else if (isProcedure && payload.procedure_datetime) {
          start = new Date(payload.procedure_datetime);
        } else if (isEnrolment && payload.enrolment_date) {
          start = new Date(payload.enrolment_date);
        }

        // Determine end date
        let end = null;
        if (isVisit && payload.end_datetime) {
          end = new Date(payload.end_datetime);
        }

        return {
          _id: item._id,
          ingestErrorId: item._id,
          internalStatus: INTERNAL_STATUS_PENDING,
          domain,
          eventId,
          patientId:
            payload.patient_identifiers?.[0]?.value ||
            payload.patient_identifier?.value,
          patientIdentifier:
            payload.patient_identifiers?.[0] || payload.patient_identifier,
          status,
          start,
          end,
          context: {
            projectId: payload.projectId,
            projectLabel: payload.projectLabel,
            milestoneId: payload.milestoneId,
            milestoneLabel: payload.milestoneLabel,
            patientName: payload.patient_name,
            clinicianName: payload.provider?.provider_name,
            location: payload.care_site?.care_site_name,
            type: isVisit ? payload.visit_type : payload.modifier_source_value,
            description: payload.description,
            notes: payload.note,
            // Enrolment specific fields - phones/emails are arrays from Recipients
            phones: payload.phones || [],
            emails: payload.emails || [],
            participationId: payload.participationId,
            trackId: payload.trackId,
            trackLabel: payload.trackLabel,
          },
          reviewQueueMetadata: {
            severity,
            type,
            reasons,
          },
        };
      });
  }, [reviewQueueItems]);

  const tableTitle = useMemo(() => {
    if (clinicalEventType === 'Visit') {
      return t('appointmentsWithCount', {
        count: total,
      });
    }

    if (clinicalEventType === 'Procedure') {
      return t('proceduresWithCount', {
        count: total,
      });
    }

    if (clinicalEventType === 'Enrolment') {
      return t('enrolmentsWithCount', {
        count: total,
      });
    }

    return '';
  }, [total, clinicalEventType, t]);

  const clinicalEventStatusOptions = useMemo(() => {
    switch (clinicalEventType) {
      case CLINICAL_EVENT_TYPE_APPOINTMENT:
        return getAppointmentStatusOptions({ t });
      case CLINICAL_EVENT_TYPE_PROCEDURE:
        return getProcedureStatusOptions({ t });
      case CLINICAL_EVENT_TYPE_ENROLMENT:
        return getEnrolmentStatusOptions({ t });
      default:
        return [
          { label: t('all'), value: 'all' },
          { label: t('states:booked'), value: 'booked' },
          { label: t('states:completed'), value: 'completed' },
          { label: t('states:noShow'), value: 'no show' },
          { label: t('states:canceled'), value: 'cancelled' },
        ];
    }
  }, [clinicalEventType, t]);

  const columns = useMemo(() => {
    switch (clinicalEventType) {
      case CLINICAL_EVENT_TYPE_APPOINTMENT:
        return getAppointmentColumns({
          language,
          t,
        });
      case CLINICAL_EVENT_TYPE_PROCEDURE:
        return getProcedureColumns({
          language,
          t,
        });
      case CLINICAL_EVENT_TYPE_ENROLMENT:
        return getEnrolmentColumns({
          language,
          t,
        });
      default:
        return [];
    }
  }, [clinicalEventType, language, t]);

  const pagination = useMemo(
    () => ({
      total,
      current: page,
      pageSize,
      pageSizeOptions: ['10', '15', '20'],
      size: 'small',
      showSizeChanger: true,
      showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
      onChange: (newPage) => {
        setPage(newPage);
      },
      onShowSizeChange: (current, newPageSize) => {
        setPageSize(newPageSize);
        setPage(1);
      },
    }),
    [total, page, pageSize],
  );

  const handleSelectRecord = useCallback((record) => {
    setIsDialogVisible(true);
    setCurrentRecord(record);
  }, []);

  const handlePatientNameInputChange = (value) => {
    setPatientNameInput(value);
  };

  const commitPatientName = useCallback(() => {
    if (patientNameInput !== patientName) {
      setPatientName(patientNameInput);
      setPage(1);
    }
  }, [patientNameInput, patientName]);

  const handlePatientNameKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        commitPatientName();
      }
    },
    [commitPatientName],
  );

  const handleClinicalEventTypeChange = (value) => {
    setClinicalEventType(value);
    setPage(1);
    setClinicalEventStatus('all');
  };

  const handleClinicianChange = (newClinicianId) => {
    if (newClinicianId === CLINICIAN_OPTION_ALL) {
      setClinicianId(null);
    } else {
      const numericId =
        typeof newClinicianId === 'string'
          ? parseInt(newClinicianId, 10)
          : newClinicianId;
      setClinicianId(numericId);
    }
    setPage(1);
  };

  const clinicianOptions = useMemo(() => {
    const options = [
      { value: CLINICIAN_OPTION_ALL, label: t('all') },
      ...clinicians,
    ];
    return options;
  }, [clinicians, t]);

  const handleSave = useCallback(
    async (values, shouldProcess = false) => {
      if (!currentRecord?.ingestErrorId) {
        console.error('No ingest error ID found');
        return;
      }

      try {
        if (shouldProcess) {
          const updates = {};
          if (values.patientId) {
            updates.patientIdentifier = {
              namespace:
                currentRecord.patientIdentifier?.namespace ||
                'external/omop_person_id',
              value: values.patientId,
            };
          }

          if (
            values.patientName &&
            values.patientName !== currentRecord.context?.patientName
          ) {
            updates.patientName = values.patientName;
          }

          if (values.projectId) {
            updates.projectId = values.projectId;
          }

          if (values.milestoneId) {
            updates.milestoneId = values.milestoneId;
          }

          if (values.linkedEventId) {
            updates.eventId = values.linkedEventId;
          }

          // Enrolment-specific fields
          if (values.phoneNumber) {
            updates.phoneNumber = values.phoneNumber;
          }

          if (values.email) {
            updates.email = values.email;
          }

          if (values.trackId) {
            updates.trackId = values.trackId;
          }

          await resolveItem(currentRecord.ingestErrorId, updates);
        }
      } catch (error) {
        console.error('Error handling save:', error);
        throw error;
      }
    },
    [currentRecord, resolveItem],
  );

  const handleDelete = useCallback(async () => {
    if (!currentRecord?.ingestErrorId) {
      console.error('No ingest error ID found');
      return;
    }

    try {
      await deleteItem(currentRecord.ingestErrorId);
    } catch (error) {
      console.error('Error deleting item:', error);
      throw error;
    }
  }, [currentRecord, deleteItem]);

  return (
    <>
      <Layout
        title={t('screens:episodesOfCare.needsReviewQueue.title')}
        description={t('screens:episodesOfCare.needsReviewQueue.description')}
        sidebar={{
          statistics: (
            <Card
              data-testid="card-pending-items"
              title={t('pendingItems')}
              bordered
            >
              <div className="cluster-2 items-baseline">
                <span
                  data-testid="card-pending-items-value"
                  className="text-xl font-semibold"
                >
                  {total}
                </span>
                <span className="text-sm">{t('requiringReview')}</span>
              </div>
            </Card>
          ),
          filters: (
            <>
              <FormFieldSearch
                data-testid="filter-clinical-event-type"
                label={t('forms:clinicalEventType.label')}
                options={getClinicalEventTypeOptions({ t })}
                input={{
                  value: clinicalEventType,
                  onChange: handleClinicalEventTypeChange,
                }}
                meta={{}}
              />
              {clinicalEventStatusOptions && (
                <FormFieldSearch
                  data-testid="filter-status"
                  label={t('forms:status.label')}
                  options={clinicalEventStatusOptions}
                  input={{
                    value: clinicalEventStatus,
                    onChange: setClinicalEventStatus,
                  }}
                  meta={{}}
                />
              )}
              <FormFieldText
                data-testid="filter-patient"
                label={t('forms:patient.label')}
                placeholder={t('forms:patient.placeholder')}
                input={{
                  value: patientNameInput,
                  onChange: handlePatientNameInputChange,
                  onBlur: commitPatientName,
                  onKeyDown: handlePatientNameKeyDown,
                }}
                meta={{}}
              />
              <FormFieldSearch
                data-testid="filter-clinician"
                label={t('forms:clinician.label')}
                options={clinicianOptions}
                input={{
                  value: clinicianId || CLINICIAN_OPTION_ALL,
                  onChange: handleClinicianChange,
                }}
                meta={{}}
                loading={cliniciansLoading}
              />
            </>
          ),
        }}
      >
        <Card className="stack-4" bordered>
          <Table
            title={tableTitle}
            dataSource={pendingRecords}
            columns={columns}
            pagination={pagination}
            onRow={(record) => ({
              onClick: () => handleSelectRecord(record),
            })}
          />
        </Card>
      </Layout>
      {currentRecord && currentRecord.domain === 'visit' && (
        <AppointmentDetails
          key={currentRecord._id}
          appointment={currentRecord}
          visible={isDialogVisible}
          onCancel={() => setIsDialogVisible(false)}
          onSave={handleSave}
          onDelete={handleDelete}
          isEditable
        />
      )}
      {currentRecord && currentRecord.domain === 'procedure' && (
        <ProcedureDetails
          key={currentRecord._id}
          procedure={currentRecord}
          visible={isDialogVisible}
          onCancel={() => setIsDialogVisible(false)}
          onSave={handleSave}
          onDelete={handleDelete}
          isEditable
        />
      )}
      {currentRecord && currentRecord.domain === 'enrolment' && (
        <EnrolmentDetails
          key={currentRecord._id}
          enrolment={currentRecord}
          visible={isDialogVisible}
          onCancel={() => setIsDialogVisible(false)}
          onSave={handleSave}
          onDelete={handleDelete}
          isEditable
        />
      )}
    </>
  );
};

export default NeedsReviewQueue;
